import React, { useState } from "react";
import { CheckCircle2, AlertTriangle, XCircle, Link2Off, Link2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatNum, extractErrorMessage } from "@/lib/api";
import { TahsilatMonthsPicker } from "@/components/prim/TahsilatMonthsPicker";

const LABELS = { tarih: "ay", tahsilat: "tahsilat", vekalet: "vekalet", giden: "giden", "araştıran": "araştıran", "müvekkil": "müvekkil" };
const fmtV = (v) => (typeof v === "number" ? formatNum(v) : v || "—");

const STYLE = {
  tam: { cls: "text-emerald-700", Icon: CheckCircle2, text: "Tahsilatla uyuşuyor" },
  kismi: { cls: "text-amber-700", Icon: AlertTriangle, text: "Kısmi uyuşma" },
  uyusmuyor: { cls: "text-rose-700", Icon: XCircle, text: "Tahsilatla uyuşmuyor" },
  yok: { cls: "text-slate-400", Icon: Link2Off, text: "Tahsilatta kayıt yok" },
  ay_yok: { cls: "text-slate-500", Icon: Link2Off, text: "Bu ay tahsilat yok" },
};

export function BulkTahsilatFillButton({ ids, onDone }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!window.confirm(`Seçili ${ids.length} primde uyuşmayan vekalet, araştıran ve eksik gidenler tahsilattan doldurulsun mu?`)) return;
    setBusy(true);
    try {
      const r = await api.post("/prim/apply-tahsilat-bulk", { ids });
      const { updated = 0, skipped = 0 } = r.data || {};
      if (updated) toast.success(`${updated} prim tahsilattan dolduruldu${skipped ? ` · ${skipped} primde doldurulacak değer yoktu` : ""}`);
      else toast.info("Seçili primlerde tahsilattan doldurulacak farklı değer yok");
      onDone?.();
    } catch (e) {
      toast.error(extractErrorMessage(e, "Toplu doldurma başarısız"));
    } finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={run} disabled={busy} data-testid="pending-bulk-tahsilat-fill"
      title="Seçili primlerde vekalet, araştıran ve eksik gidenleri tahsilat kayıtlarından doldur"
      className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-semibold text-sm transition-colors disabled:opacity-50">
      <Link2 size={16} /> {busy ? "Dolduruluyor..." : `Tahsilattan Doldur (${ids.length})`}
    </button>
  );
}

function OldTahsilatNote({ primId, match, onApplied }) {
  const [busy, setBusy] = useState(false);
  const chk = match.eski_check;
  const call = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); onApplied?.(); }
    catch (e) { toast.error(extractErrorMessage(e, "Kaydedilemedi")); }
    finally { setBusy(false); }
  };
  const check = () => {
    const note = window.prompt("Kontrol notu (ör. aynı infaz, taksitli ödeme)");
    if (note && note.trim()) call(() => api.post(`/prim/${primId}/eski-tahsilat-check`, { note: note.trim() }), "Kontrol edildi olarak işaretlendi");
  };
  const undo = () => call(() => api.delete(`/prim/${primId}/eski-tahsilat-check`), "Kontrol notu geri alındı");
  const son = match.son_tahsilat_tarihi.split("-").reverse().join(".");
  if (chk) {
    return (
      <span className="block font-normal text-slate-400" data-testid={`tahsilat-match-old-checked-${primId}`}
        title={`Son tahsilat ${son} · ${chk.by || ""} · ${new Date(chk.at).toLocaleDateString("tr-TR")}`}>
        <CheckCircle2 size={10} className="inline -mt-0.5" /> Kontrol edildi · {chk.note}
        <button type="button" disabled={busy} onClick={undo} data-testid={`tahsilat-old-undo-${primId}`}
          className="ml-1 underline hover:text-slate-600 disabled:opacity-50">geri al</button>
      </span>
    );
  }
  return (
    <span className="block font-normal text-amber-700" data-testid={`tahsilat-match-old-${primId}`}
      title="Prim tarihi ile son tahsilat tarihi arasında 3 aydan uzun süre var">
      Son tahsilat {son} · 3 aydan uzun ara, aynı infaz olmayabilir
      <button type="button" disabled={busy} onClick={check} data-testid={`tahsilat-old-check-${primId}`}
        className="ml-1 px-1 rounded border border-amber-300 bg-amber-50 font-semibold hover:bg-amber-100 disabled:opacity-50">Kontrol edildi</button>
    </span>
  );
}

export function TahsilatMatchNote({ primId, match, onApplied }) {
  const [busy, setBusy] = useState(false);
  if (!match) return null;
  const st = STYLE[match.status] || STYLE.yok;
  const sug = match.suggest || {};

  const apply = async (fields) => {
    setBusy(true);
    try {
      await api.post(`/prim/${primId}/apply-tahsilat`, { fields });
      toast.success("Tahsilattan dolduruldu");
      onApplied?.();
    } catch (e) {
      toast.error(extractErrorMessage(e, "Doldurulamadı"));
    } finally { setBusy(false); }
  };

  const title = (match.bad || []).map((b) => `${LABELS[b.label] || b.label}: prim ${fmtV(b.prim)} / tahsilat ${fmtV(b.tahsilat)}`).join("\n");
  return (
    <div className={`mt-1 text-[10px] font-semibold leading-snug ${st.cls}`} data-testid={`tahsilat-match-${primId}`} data-status={match.status} title={title}>
      <span className="inline-flex items-center gap-1">
        <st.Icon size={10} /> {st.text}
        {match.count > 1 && <span className="font-normal opacity-80">({match.count} tahsilat toplamı)</span>}
      </span>
      {match.status === "ay_yok" && (
        <span className="block font-normal" data-testid={`tahsilat-match-other-months-${primId}`}>
          {match.prim_ay} ayında kayıt yok
        </span>
      )}
      {(match.months || []).some((m) => !m.current) && (
        <TahsilatMonthsPicker primId={primId} months={match.months} onApplied={onApplied} />
      )}
      {(match.bad || []).length > 0 && (
        <span className="block font-normal" data-testid={`tahsilat-match-bad-${primId}`}>
          {(match.bad || []).map((b) => `${LABELS[b.label] || b.label} uyuşmuyor`).join(", ")}
        </span>
      )}
      {(sug.vekalet_ucreti != null || sug.arastiran_kisi_id || (sug.giden_add || []).length > 0) && (
        <span className="flex flex-wrap gap-1 mt-0.5">
          {(sug.giden_add || []).length > 0 && (
            <button type="button" disabled={busy} onClick={() => apply(["giden_kisiler"])} data-testid={`tahsilat-fill-giden-${primId}`}
              className="px-1.5 py-0.5 rounded border border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 disabled:opacity-50">
              Giden ekle: {sug.giden_add_names.join(", ")}
            </button>
          )}
          {sug.vekalet_ucreti != null && (
            <button type="button" disabled={busy} onClick={() => apply(["vekalet_ucreti"])} data-testid={`tahsilat-fill-vekalet-${primId}`}
              className="px-1.5 py-0.5 rounded border border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 disabled:opacity-50">
              Vekaleti doldur: {formatNum(sug.vekalet_ucreti)}
            </button>
          )}
          {sug.arastiran_kisi_id && (
            <button type="button" disabled={busy} onClick={() => apply(["arastiran_kisi_id"])} data-testid={`tahsilat-fill-arastiran-${primId}`}
              className="px-1.5 py-0.5 rounded border border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 disabled:opacity-50">
              Araştıran: {sug.arastiran_ad}
            </button>
          )}
        </span>
      )}
      {!match.infaz && (
        <span className="mt-0.5 flex items-center gap-1 font-semibold text-rose-600" data-testid={`tahsilat-match-no-infaz-${primId}`}>
          <AlertTriangle size={10} /> İnfaz kaydı yok
        </span>
      )}
      {match.eski_tahsilat && <OldTahsilatNote primId={primId} match={match} onApplied={onApplied} />}
    </div>
  );
}
